/**
 * Materializes theme-level `animationSettings` onto a theme's template
 * FlowDocument — the single place both editor-cascade and apply flows rely on.
 *
 * Writes two planes:
 * 1. FlowDocument-level defaults: `defaultTransition` / `defaultScreenAnimation`
 *    / `perKindTransitions` (what the version seam later copies to
 *    `screensGraph.*` and the publish pipeline bakes into the runtime config;
 *    per-kind transitions resolve at navigation time against the TARGET
 *    screen's kind — route.transition > perKindTransitions > defaultTransition).
 * 2. Every template screen's `content.screenAnimation` + `content.animated`
 *    (tiles stay WYSIWYG; screens added from the templates panel inherit
 *    motion verbatim). Per-kind overrides (v1: Paywall) win over the theme-wide
 *    entrance. WebEmbed is skipped — custom HTML owns its own motion.
 *
 * Semantics:
 * - `settings` absent → document returned unchanged (theme does not manage motion).
 * - field `null` (scheme "None") → default removed + `animated:false` on screens.
 * - field object → default set + entrance materialized on supported screens.
 */

import type { FlowDocument, ScreenAnimation, ScreenTransition, StepNode } from '@segmently/schema-models/v2';
import type { ThemeV2AnimationSettings } from '@modules/themes/types/themesV2.types';

/**
 * Per-StepKind transition defaults materialized from
 * `perKindOverrides[kind].transition`. Local extension of the generated
 * FlowDocument — pending upstreaming into segmently-schema (add the field to
 * FlowDocument, rebuild the tarball); until then every write/read goes
 * through `buildPerKindTransitions` / the loader cast so the schema bump
 * stays a type-only change.
 */
export type PerKindTransitionsMap = Partial<Record<string, ScreenTransition>>;

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** StepKinds whose motion the theme must not manage. */
const ANIMATION_UNSUPPORTED_KINDS = new Set<string>(['WebEmbed']);

export function isAnimationSupportedKind(screenType: string | undefined): boolean {
  return !!screenType && !ANIMATION_UNSUPPORTED_KINDS.has(screenType);
}

/**
 * Collects `perKindOverrides[kind].transition` into the runtime per-kind map.
 * Returns null when no kind carries a transition override — callers treat
 * null as "erase" so switching to a scheme without overrides (Subtle/None)
 * removes a previously materialized map instead of leaving it stale.
 */
export function buildPerKindTransitions(
  settings: ThemeV2AnimationSettings | null | undefined
): PerKindTransitionsMap | null {
  const overrides = settings?.perKindOverrides;
  if (!overrides) return null;
  const map: PerKindTransitionsMap = {};
  let hasAny = false;
  for (const [kind, override] of Object.entries(overrides)) {
    if (override?.transition) {
      map[kind] = cloneJson(override.transition);
      hasAny = true;
    }
  }
  return hasAny ? map : null;
}

/**
 * Version-level `screensGraph` animation defaults for a NEW onboarding
 * created from a theme (wizard path). Creation must OMIT fields rather than
 * write nulls (Firestore stores literal null; "None"/absent settings simply
 * produce no defaults). The explicit Apply→Animations seam remains the
 * erase-capable path (`updateVersionAnimationDefaults` null→deleteField).
 */
export function versionAnimationDefaultsFromSettings(
  settings: ThemeV2AnimationSettings | null | undefined
): {
  defaultTransition?: ScreenTransition;
  defaultScreenAnimation?: ScreenAnimation;
  perKindTransitions?: PerKindTransitionsMap;
} | null {
  if (!settings) return null;
  const defaults: {
    defaultTransition?: ScreenTransition;
    defaultScreenAnimation?: ScreenAnimation;
    perKindTransitions?: PerKindTransitionsMap;
  } = {};
  if (settings.transition) defaults.defaultTransition = cloneJson(settings.transition);
  if (settings.screenAnimation) defaults.defaultScreenAnimation = cloneJson(settings.screenAnimation);
  const perKind = buildPerKindTransitions(settings);
  if (perKind) defaults.perKindTransitions = perKind;
  return Object.keys(defaults).length > 0 ? defaults : null;
}

/** Effective entrance for a screen kind: per-kind override wins over theme-wide. */
export function resolveEntranceForKind(
  settings: ThemeV2AnimationSettings,
  screenType: string | undefined
): ScreenAnimation | null | undefined {
  const override = screenType
    ? settings.perKindOverrides?.[screenType as keyof NonNullable<ThemeV2AnimationSettings['perKindOverrides']>]
    : undefined;
  if (override && override.screenAnimation !== undefined) {
    return override.screenAnimation;
  }
  return settings.screenAnimation;
}

/** Applies the entrance decision to a single screen's content (in place on a clone). */
function applyEntranceToScreen(screen: StepNode, entrance: ScreenAnimation | null | undefined): void {
  if (entrance === undefined) return; // theme does not manage entrance
  const content = screen.content as unknown as Record<string, unknown>;
  if (entrance === null) {
    delete content.screenAnimation;
    content.animated = false;
    return;
  }
  content.screenAnimation = cloneJson(entrance);
  content.animated = true;
}

export function applyAnimationSettingsToFlowDocument(
  settings: ThemeV2AnimationSettings | null | undefined,
  flowDocument: FlowDocument
): FlowDocument {
  if (!settings) return flowDocument;

  const doc = cloneJson(flowDocument) as FlowDocument & {
    defaultTransition?: unknown;
    defaultScreenAnimation?: unknown;
    perKindTransitions?: unknown;
  };

  // Always recompute the per-kind map: switching from a scheme WITH overrides
  // (e.g. Smooth + Calm Paywall) to one without must erase the stale map.
  const perKindTransitions = buildPerKindTransitions(settings);
  if (perKindTransitions) {
    doc.perKindTransitions = perKindTransitions;
  } else {
    delete doc.perKindTransitions;
  }

  if (settings.transition !== undefined) {
    if (settings.transition === null) {
      delete doc.defaultTransition;
    } else {
      doc.defaultTransition = cloneJson(settings.transition);
    }
  }
  if (settings.screenAnimation !== undefined) {
    if (settings.screenAnimation === null) {
      delete doc.defaultScreenAnimation;
    } else {
      doc.defaultScreenAnimation = cloneJson(settings.screenAnimation);
    }
  }

  for (const screen of Object.values(doc.screens ?? {})) {
    if (!isAnimationSupportedKind(screen.screenType as string)) continue;
    applyEntranceToScreen(screen as StepNode, resolveEntranceForKind(settings, screen.screenType as string));
  }

  return doc;
}
